import React, { useEffect, Component } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, ChevronRight } from 'lucide-react';
import { SERVICE_CITIES, SERVICE_PRODUCT_SLUGS, getServiceProduct } from '../data/cityMaterialContent';
// Sitemap-style index page that links to every generated City × Material
// landing page. Internal links here power both human navigation and
// search-engine discovery.
export function ServiceAreas() {
  useEffect(() => {
    const orig = document.title;
    document.title = 'Service Areas — 253Gravel Delivery to Tacoma, Puyallup, Auburn, Kent, Renton & Federal Way';
    return () => {
      document.title = orig;
    };
  }, []);
  const products = SERVICE_PRODUCT_SLUGS.map(s => getServiceProduct(s)).filter(Boolean) as {
    product: any;
    category: any;
  }[];
  return <main className="bg-[#fdfbf7] dark:bg-[#222222] min-h-screen" data-id="element-2894">
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6" data-id="element-2895">
        <ol className="flex items-center gap-1.5 text-xs text-stone-500 dark:text-stone-400" data-id="element-2896">
          <li data-id="element-2897">
            <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-2898">
              Home
            </Link>
          </li>
          <ChevronRight className="w-3 h-3" data-id="element-2899" />
          <li className="text-stone-900 dark:text-white font-semibold" aria-current="page" data-id="element-2900">
            Service Areas
          </li>
        </ol>
      </nav>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-10" data-id="element-2901">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-2902">
          <div className="inline-flex items-center gap-2 mb-3" data-id="element-2903">
            <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-2904" />
            <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-2905">
              South Sound · King & Pierce County
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900 dark:text-white leading-[1.05]" data-id="element-2906">
            Service Areas
          </h1>
          <p className="mt-4 text-stone-600 dark:text-stone-300 max-w-3xl" data-id="element-2907">
            We deliver gravel, topsoil, bark, and drain rock across South King
            and Pierce County. Tap a city to see materials, pricing, and
            delivery info specific to your neighborhood.
          </p>
        </motion.div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-id="element-2908">
        <div className="space-y-10" data-id="element-2909">
          {SERVICE_CITIES.map(city => <div key={city.slug} id={city.slug} className="scroll-mt-24 p-6 md:p-8 rounded-2xl bg-white dark:bg-white/[0.03] border border-stone-200 dark:border-white/10" data-id="element-2910">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4" data-id="element-2911">
                <div data-id="element-2912">
                  <h2 className="font-heading text-2xl font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-2913">
                    {city.name}, {city.state}
                  </h2>
                  <p className="mt-1 text-xs font-bold text-brand-orange uppercase tracking-wider" data-id="element-2914">
                    {city.county} County · Zone {city.zone} ·{' '}
                    {city.neighborhoods.length}+ neighborhoods
                  </p>
                </div>
                <Link to={`/delivery-zones?address=${encodeURIComponent(city.geocodeHint)}`} className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-orange hover:underline" data-id="element-2915">
                  See on map
                  <ArrowRight className="w-3 h-3" data-id="element-2916" />
                </Link>
              </div>

              <p className="text-sm text-stone-600 dark:text-stone-300 mb-4" data-id="element-2917">
                {city.blurb}
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-2918">
                {products.map(({
              product
            }) => <Link key={product.slug} to={`/gravel-delivery/${city.slug}/${product.slug}`} className="group flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-stone-50 dark:bg-white/[0.04] border border-stone-200 dark:border-white/10 hover:border-brand-orange transition-colors" data-id="element-2919">
                    <div className="min-w-0" data-id="element-2920">
                      <div className="text-sm font-bold text-stone-900 dark:text-white truncate group-hover:text-brand-orange transition-colors" data-id="element-2921">
                        {product.name}
                      </div>
                      <div className="text-[11px] text-stone-500 dark:text-stone-400" data-id="element-2922">
                        ${product.pricePerYard}/yd in {city.name}
                      </div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-stone-400 group-hover:text-brand-orange shrink-0" data-id="element-2923" />
                  </Link>)}
              </div>
            </div>)}
        </div>

        <div className="mt-12 p-6 rounded-2xl bg-stone-900 dark:bg-black border border-stone-800 text-center" data-id="element-2924">
          <h2 className="font-heading text-xl font-bold text-white" data-id="element-2925">
            Not seeing your city?
          </h2>
          <p className="mt-2 text-sm text-stone-300" data-id="element-2926">
            We deliver well beyond these cities. Check the zone map or call us
            for a custom quote.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-3" data-id="element-2927">
            <Link to="/delivery-zones" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-orange hover:bg-brand-orange-hover text-white font-bold text-sm transition-colors" data-id="element-2928">
              View Zone Map
              <ArrowRight className="w-3.5 h-3.5" data-id="element-2929" />
            </Link>
            <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white font-bold text-sm transition-colors" data-id="element-2930">
              (206) 619-3742
            </a>
          </div>
        </div>
      </section>
    </main>;
}